/**
 * Precision CAP Portal — shared chrome.
 *
 * The portal is the read-mostly view a vendor (FDR contact) sees after they
 * accept an invitation. It is intentionally lighter than Core: no sidebar,
 * no module switcher, no nested navigation. One page, one CAP, plain English.
 *
 * Header: small PCG mark + "Precision CAP Portal" wordmark on the left,
 * the signed-in person's name and a Logout button on the right when there
 * is an active session.
 */

import React from 'react';

const API = process.env.REACT_APP_API_URL || '';

export default function PortalLayout({ user, children, onLoggedOut }) {
  const handleLogout = async () => {
    try {
      await fetch(`${API}/api/portal/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch {
      // logout is best-effort — fall through to client-side reset
    }
    if (onLoggedOut) onLoggedOut();
    else window.location.href = '/login';
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800 font-sans">
      <header className="sticky top-0 z-30 bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-lg bg-slate-900 text-white flex items-center justify-center font-semibold text-sm shrink-0">
              PCG
            </div>
            <div className="min-w-0">
              <div className="text-[15px] font-semibold text-slate-900 leading-tight truncate">
                Precision CAP Portal
              </div>
              <div className="text-[12px] text-slate-500 leading-tight">
                One license for everything your compliance team handles every week.
              </div>
            </div>
          </div>

          {user && (
            <div className="flex items-center gap-3">
              <div className="text-right hidden sm:block">
                <div className="text-[13px] font-medium text-slate-800 leading-tight">
                  {user.full_name || user.email}
                </div>
                {user.full_name && (
                  <div className="text-[12px] text-slate-500 leading-tight truncate max-w-[200px]">
                    {user.email}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={handleLogout}
                className="px-3 py-1.5 text-[13px] font-medium text-slate-700 bg-white ring-1 ring-slate-200 rounded-lg hover:bg-slate-50 transition"
              >
                Sign out
              </button>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-10">
        {children}
      </main>

      <footer className="max-w-5xl mx-auto px-6 pb-10 pt-6 text-[12px] text-slate-400">
        Built by people who have worked inside compliance, not by a team that read a brochure.
      </footer>
    </div>
  );
}
